import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { FuseqHeaders } from '../../FuseQ/fuseq.headers'
import { environment } from '../../../environments/environment'

export interface MediationTag {
  id: number
  name: string
  color: string
  categoryId?: number | null
  parentId?: number | null
  description?: string
  icon?: string
  order?: number
  totalCount?: number
}

export interface MediationTagCategory {
  id: number
  name: string
  description?: string
  icon?: string
  color?: string
  order?: number
}

export interface MediationCustomFilter {
  id: number
  name: string
  tagIds: number[]
  carriers: string[]
  tecnologie: string[]
  celle: string[]
  frazList: string[]
  source?: 'manual' | 'excel'
  owner?: string | null
  shared?: boolean
}

export interface MediationAssignment {
  id: number
  entityId: string
  entityType: 'location' | 'device'
  tagId: number
}

export interface MediationBootstrap {
  locations: any[]
  tags: MediationTag[]
  customFilters: MediationCustomFilter[]
  assignments: MediationAssignment[]
  categories: MediationTagCategory[]
}

@Injectable({
  providedIn: 'root'
})
export class AwpMediationLayerApiService {
  constructor (
    private readonly http: HttpClient,
    private readonly fuseqHeaders: FuseqHeaders
  ) { }

  private base (): string {
    return `${environment.API_URL}/mediationLayer`
  }

  bootstrap (frazionario?: string): Observable<MediationBootstrap> {
    const query = frazionario ? `?frazionario=${encodeURIComponent(frazionario)}` : ''
    return this.http.get<MediationBootstrap>(`${this.base()}/bootstrap${query}`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getLocations (frazionario?: string): Observable<any[]> {
    const query = frazionario ? `?frazionario=${encodeURIComponent(frazionario)}` : ''
    return this.http.get<any[]>(`${this.base()}/locations${query}`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getCategories (): Observable<MediationTagCategory[]> {
    return this.http.get<MediationTagCategory[]>(`${this.base()}/tag-categories`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  createCategory (payload: Partial<MediationTagCategory>): Observable<MediationTagCategory> {
    return this.http.post<MediationTagCategory>(`${this.base()}/tag-categories`, payload, this.fuseqHeaders.setTokenHeaders('POST'))
  }

  updateCategory (id: number, payload: Partial<MediationTagCategory>): Observable<MediationTagCategory> {
    return this.http.put<MediationTagCategory>(`${this.base()}/tag-categories/${id}`, payload, this.fuseqHeaders.setTokenHeaders('PUT'))
  }

  deleteCategory (id: number): Observable<any> {
    return this.http.delete(`${this.base()}/tag-categories/${id}`, this.fuseqHeaders.setTokenHeaders('DELETE'))
  }

  getTags (): Observable<MediationTag[]> {
    return this.http.get<MediationTag[]>(`${this.base()}/tags`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  createTag (payload: Partial<MediationTag>): Observable<MediationTag> {
    return this.http.post<MediationTag>(`${this.base()}/tags`, payload, this.fuseqHeaders.setTokenHeaders('POST'))
  }

  updateTag (id: number, payload: Partial<MediationTag>): Observable<MediationTag> {
    return this.http.put<MediationTag>(`${this.base()}/tags/${id}`, payload, this.fuseqHeaders.setTokenHeaders('PUT'))
  }

  deleteTag (id: number): Observable<any> {
    return this.http.delete(`${this.base()}/tags/${id}`, this.fuseqHeaders.setTokenHeaders('DELETE'))
  }

  getCustomFilters (): Observable<MediationCustomFilter[]> {
    return this.http.get<MediationCustomFilter[]>(`${this.base()}/custom-filters`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  createCustomFilter (payload: Partial<MediationCustomFilter>): Observable<MediationCustomFilter> {
    return this.http.post<MediationCustomFilter>(`${this.base()}/custom-filters`, payload, this.fuseqHeaders.setTokenHeaders('POST'))
  }

  updateCustomFilter (id: number, payload: Partial<MediationCustomFilter>): Observable<MediationCustomFilter> {
    return this.http.put<MediationCustomFilter>(`${this.base()}/custom-filters/${id}`, payload, this.fuseqHeaders.setTokenHeaders('PUT'))
  }

  deleteCustomFilter (id: number): Observable<any> {
    return this.http.delete(`${this.base()}/custom-filters/${id}`, this.fuseqHeaders.setTokenHeaders('DELETE'))
  }

  getAssignments (fraz?: string): Observable<MediationAssignment[]> {
    const query = fraz ? `?fraz=${encodeURIComponent(fraz)}` : ''
    return this.http.get<MediationAssignment[]>(`${this.base()}/assignments${query}`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  assignTags (ids: string[], tagIds: number[]): Observable<{ assigned: boolean; count: number; tags: MediationTag[] }> {
    const body = { ids, tagIds }
    return this.http.post<{ assigned: boolean; count: number; tags: MediationTag[] }>(`${this.base()}/assignments`, body, this.fuseqHeaders.setTokenHeaders('POST'))
  }

  removeTags (ids: string[], tagIds: number[]): Observable<{ removed: boolean; count: number; tags: MediationTag[] }> {
    const body = { ids, tagIds }
    return this.http.request<{ removed: boolean; count: number; tags: MediationTag[] }>('delete', `${this.base()}/assignments`, {
      body,
      ...this.fuseqHeaders.setTokenHeaders('DELETE')
    })
  }

  getFrazionarioTags (fraz: string): Observable<MediationTag[]> {
    return this.http.get<MediationTag[]>(`${this.base()}/frazionario-tags/${encodeURIComponent(fraz)}`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getFrazionarioTagsBatch (frazionarioCodes: string[]): Observable<Record<string, MediationTag[]>> {
    return this.http.post<Record<string, MediationTag[]>>(`${this.base()}/frazionario-tags/batch`, { frazionarioCodes }, this.fuseqHeaders.setTokenHeaders('POST'))
  }
}
